/**
 * BookingDetail — /crm/bookings/:bookingId
 * Full workspace for a customer booking: Overview, Contract, Payments, Event Linkage, Activity
 */
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft, FileText, CreditCard, CalendarDays, Activity,
  CheckCircle2, XCircle, Clock, AlertCircle, ChevronRight,
  Plus, Pencil, Loader2, RefreshCw, Building2, User,
} from "lucide-react";
import { bookingApi } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import { hasCapability } from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { InterventionBanner } from "@/components/InterventionBanner";
import { EventStageBar } from "@/components/EventStageBar";
import { WorkflowActionBar } from "@/components/WorkflowActionBar";

// ── Booking lifecycle stages (for EventStageBar) ──────────────────────────────
const BOOKING_STAGES = [
  { id: "DRAFT",              label: "Draft"      },
  { id: "PENDING_ACCEPTANCE", label: "Pending"    },
  { id: "CONFIRMED",          label: "Confirmed"  },
  { id: "COMPLETED",          label: "Completed"  },
];
const BOOKING_TERMINAL = { id: "CANCELLED", label: "Cancelled" };

// ── Palette ────────────────────────────────────────────────────────────────────
const C = {
  ivory:   "#FBF7F2",
  sand:    "#FAF6F0",
  gold:    "#C9A15F",
  charcoal:"#3A3330",
  slate:   "#7A706A",
  divider: "rgba(201,161,95,0.15)",
};

// ── Status badge ──────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  DRAFT:              { bg: "#F3F4F6", text: "#374151", border: "#D1D5DB", label: "Draft" },
  PENDING_ACCEPTANCE: { bg: "#FEF3C7", text: "#92400E", border: "#FCD34D", label: "Pending Acceptance" },
  CONFIRMED:          { bg: "#D1FAE5", text: "#065F46", border: "#6EE7B7", label: "Confirmed" },
  COMPLETED:          { bg: "#DBEAFE", text: "#1E40AF", border: "#93C5FD", label: "Completed" },
  CANCELLED:          { bg: "#FEE2E2", text: "#991B1B", border: "#FCA5A5", label: "Cancelled" },
};

const MILESTONE_STATUS_CONFIG = {
  PENDING:  { bg: "#F3F4F6", text: "#374151", label: "Pending" },
  INVOICED: { bg: "#FEF3C7", text: "#92400E", label: "Invoiced" },
  PAID:     { bg: "#D1FAE5", text: "#065F46", label: "Paid" },
  WAIVED:   { bg: "#DBEAFE", text: "#1E40AF", label: "Waived" },
  OVERDUE:  { bg: "#FEE2E2", text: "#991B1B", label: "Overdue" },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.DRAFT;
  return (
    <span
      className="px-2.5 py-1 text-xs font-medium rounded-full"
      style={{ background: cfg.bg, color: cfg.text, border: `1px solid ${cfg.border}` }}
    >
      {cfg.label}
    </span>
  );
}

const TABS = [
  { id: "overview",  label: "Overview",      icon: FileText },
  { id: "contract",  label: "Contract",       icon: FileText },
  { id: "payments",  label: "Payments",       icon: CreditCard },
  { id: "event",     label: "Event Linkage",  icon: CalendarDays },
  { id: "activity",  label: "Activity",       icon: Activity },
];

const fmtCurrency = (v, currency = "INR") =>
  v != null ? `${currency === "INR" ? "₹" : currency} ${Number(v).toLocaleString("en-IN")}` : "—";

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—";
const fmtDateTime = (d) => d ? new Date(d).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

// ── Confirm modal ─────────────────────────────────────────────────────────────
function ConfirmBookingModal({ booking, onClose, onSuccess }) {
  const [form, setForm] = useState({ acceptance_method: "SYSTEM_GENERATED", accepted_by_name: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.accepted_by_name.trim()) e.accepted_by_name = "Name of accepting party is required";
    return e;
  };

  const handleConfirm = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setLoading(true);
    try {
      await bookingApi.confirm(booking.id, form);
      toast.success("Booking confirmed");
      onSuccess();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to confirm");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Confirm Booking</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <Label>Acceptance Method</Label>
            <select
              data-testid="acceptance-method-select"
              className="w-full mt-1 border rounded px-3 py-2 text-sm"
              value={form.acceptance_method}
              onChange={e => setForm(f => ({ ...f, acceptance_method: e.target.value }))}
            >
              {["DIGITAL_SIGNATURE","EMAIL_CONFIRMATION","IN_PERSON","SYSTEM_GENERATED"].map(m => (
                <option key={m} value={m}>{m.replace(/_/g, " ")}</option>
              ))}
            </select>
          </div>
          <div>
            <Label>
              Accepted By Name <span className="text-red-500">*</span>
            </Label>
            <Input
              data-testid="accepted-by-name-input"
              className={`mt-1 ${errors.accepted_by_name ? "border-red-400 focus:ring-red-300" : ""}`}
              value={form.accepted_by_name}
              onChange={e => {
                setForm(f => ({ ...f, accepted_by_name: e.target.value }));
                if (errors.accepted_by_name) setErrors(p => ({ ...p, accepted_by_name: undefined }));
              }}
              placeholder="Customer representative name"
            />
            {errors.accepted_by_name && (
              <p data-testid="accepted-by-name-error" className="mt-1 text-xs text-red-500">
                {errors.accepted_by_name}
              </p>
            )}
          </div>
          <div>
            <Label>Notes</Label>
            <Input
              data-testid="confirm-notes-input"
              className="mt-1"
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Confirmation notes..."
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button
              data-testid="confirm-booking-btn"
              className="flex-1"
              style={{ background: C.gold }}
              onClick={handleConfirm}
              disabled={loading}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Confirm Booking
            </Button>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Cancel modal ──────────────────────────────────────────────────────────────
function CancelBookingModal({ booking, onClose, onSuccess }) {
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [reasonError, setReasonError] = useState("");

  const handleCancel = async () => {
    if (!reason.trim()) {
      setReasonError("Cancellation reason is required");
      return;
    }
    setLoading(true);
    try {
      await bookingApi.cancel(booking.id, { reason, notes });
      toast.success("Booking cancelled");
      onSuccess();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to cancel");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Cancel Booking</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <Label>
              Cancellation Reason <span className="text-red-500">*</span>
            </Label>
            <Input
              data-testid="cancel-reason-input"
              className={`mt-1 ${reasonError ? "border-red-400" : ""}`}
              value={reason}
              onChange={e => {
                setReason(e.target.value);
                if (reasonError) setReasonError("");
              }}
              placeholder="Reason for cancellation..."
            />
            {reasonError && (
              <p data-testid="cancel-reason-error" className="mt-0.5 text-xs text-red-500">
                {reasonError}
              </p>
            )}
          </div>
          <div>
            <Label>Additional Notes</Label>
            <Input
              className="mt-1"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Optional notes..."
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button
              data-testid="cancel-booking-submit-btn"
              className="flex-1"
              variant="destructive"
              onClick={handleCancel}
              disabled={loading}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Cancel Booking
            </Button>
            <Button variant="outline" onClick={onClose}>Back</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Add Milestone modal ───────────────────────────────────────────────────────
function AddMilestoneModal({ bookingId, onClose, onSuccess }) {
  const [form, setForm] = useState({ name: "", description: "", due_date: "", amount: "" });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleAdd = async () => {
    const e = {};
    if (!form.name.trim()) e.name = "Milestone name is required";
    if (!form.amount) e.amount = "Amount is required";
    else if (isNaN(parseFloat(form.amount)) || parseFloat(form.amount) <= 0) e.amount = "Enter a valid positive amount";
    if (Object.keys(e).length) { setErrors(e); return; }
    setLoading(true);
    try {
      await bookingApi.addMilestone(bookingId, { ...form, amount: parseFloat(form.amount) });
      toast.success("Milestone added");
      onSuccess();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add milestone");
    } finally {
      setLoading(false);
    }
  };

  const fieldDefs = [
    { label: "Milestone Name", key: "name",        type: "text",   placeholder: "e.g., Advance Payment",  required: true },
    { label: "Amount (INR)",   key: "amount",       type: "number", placeholder: "0",                      required: true },
    { label: "Due Date",       key: "due_date",     type: "date",   placeholder: "",                       required: false },
    { label: "Description",    key: "description",  type: "text",   placeholder: "Optional details...",    required: false },
  ];

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Add Payment Milestone</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          {fieldDefs.map(f => (
            <div key={f.key}>
              <Label>
                {f.label}
                {f.required && <span className="text-red-500 ml-0.5">*</span>}
              </Label>
              <Input
                data-testid={`milestone-${f.key}-input`}
                className={`mt-1 ${errors[f.key] ? "border-red-400" : ""}`}
                type={f.type}
                placeholder={f.placeholder}
                value={form[f.key]}
                onChange={e => {
                  setForm(p => ({ ...p, [f.key]: e.target.value }));
                  if (errors[f.key]) setErrors(p => ({ ...p, [f.key]: undefined }));
                }}
              />
              {errors[f.key] && (
                <p data-testid={`milestone-${f.key}-error`} className="mt-0.5 text-xs text-red-500">
                  {errors[f.key]}
                </p>
              )}
            </div>
          ))}
          <div className="flex gap-2 pt-2">
            <Button
              data-testid="add-milestone-btn"
              className="flex-1"
              style={{ background: C.gold }}
              onClick={handleAdd}
              disabled={loading}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Add Milestone
            </Button>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Overview tab ──────────────────────────────────────────────────────────────
function OverviewTab({ booking }) {
  const fields = [
    { label: "Booking Number",      value: booking.booking_number },
    { label: "Status",              value: <StatusBadge status={booking.status} /> },
    { label: "Opportunity",         value: booking.opportunity_name || booking.opportunity_id },
    { label: "Booking Value",       value: fmtCurrency(booking.booking_value, booking.currency) },
    { label: "Tax",                 value: booking.tax_amount ? fmtCurrency(booking.tax_amount, booking.currency) : "—" },
    { label: "Total Value",         value: fmtCurrency(booking.total_value, booking.currency) },
    { label: "Currency",            value: booking.currency },
    { label: "Created",             value: fmtDateTime(booking.created_at) },
    { label: "Confirmed",           value: fmtDateTime(booking.confirmed_at) },
    { label: "Cancelled",           value: fmtDateTime(booking.cancelled_at) },
  ].filter(f => f.value && f.value !== "—");

  return (
    <div className="p-6 space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {fields.map(f => (
          <div key={f.label} className="rounded-lg p-3" style={{ background: C.sand, border: `1px solid ${C.divider}` }}>
            <div className="text-xs mb-1" style={{ color: C.slate }}>{f.label}</div>
            <div className="text-sm font-medium" style={{ color: C.charcoal }}>{f.value}</div>
          </div>
        ))}
      </div>
      {booking.notes && (
        <div className="p-4 rounded-lg text-sm" style={{ background: C.sand, color: C.slate, border: `1px solid ${C.divider}` }}>
          <div className="font-medium mb-1" style={{ color: C.charcoal }}>Notes</div>
          {booking.notes}
        </div>
      )}
      {booking.cancellation_reason && (
        <div className="p-4 rounded-lg" style={{ background: "#FEE2E2", border: "1px solid #FCA5A5" }}>
          <div className="text-sm font-medium text-red-800">Cancellation Reason</div>
          <div className="text-sm text-red-700 mt-1">{booking.cancellation_reason}</div>
        </div>
      )}
    </div>
  );
}

// ── Contract tab ──────────────────────────────────────────────────────────────
function ContractTab({ booking }) {
  return (
    <div className="p-6 space-y-4">
      <div className="rounded-lg p-4" style={{ background: C.sand, border: `1px solid ${C.divider}` }}>
        <div className="text-xs mb-1 font-medium" style={{ color: C.slate }}>Accepted Proposal Revision</div>
        <div className="text-sm font-mono" style={{ color: C.charcoal }}>{booking.accepted_proposal_revision_id}</div>
      </div>
      {booking.commercial_terms ? (
        <div className="rounded-lg p-4" style={{ background: C.sand, border: `1px solid ${C.divider}` }}>
          <div className="text-xs mb-1 font-medium" style={{ color: C.slate }}>Commercial Terms</div>
          <div className="text-sm whitespace-pre-wrap" style={{ color: C.charcoal }}>{booking.commercial_terms}</div>
        </div>
      ) : (
        <div className="text-sm text-center py-6" style={{ color: C.slate }}>No commercial terms recorded yet</div>
      )}
      {booking.acceptance_evidence && (
        <div className="rounded-lg p-4" style={{ background: "#D1FAE5", border: "1px solid #6EE7B7" }}>
          <div className="text-xs font-medium text-green-800 mb-2">Acceptance Evidence</div>
          <dl className="text-sm space-y-1">
            <div className="flex gap-2">
              <dt className="text-green-700 w-32">Method:</dt>
              <dd className="font-medium text-green-900">{booking.acceptance_evidence.method?.replace(/_/g, " ")}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="text-green-700 w-32">Accepted At:</dt>
              <dd className="text-green-900">{fmtDateTime(booking.acceptance_evidence.accepted_at)}</dd>
            </div>
            {booking.acceptance_evidence.accepted_by_name && (
              <div className="flex gap-2">
                <dt className="text-green-700 w-32">Accepted By:</dt>
                <dd className="text-green-900">{booking.acceptance_evidence.accepted_by_name}</dd>
              </div>
            )}
          </dl>
        </div>
      )}
      {(booking.amendments || []).length > 0 && (
        <div>
          <div className="text-sm font-medium mb-2" style={{ color: C.charcoal }}>Amendments ({booking.amendments.length})</div>
          <div className="space-y-2">
            {booking.amendments.map(a => (
              <div key={a.amendment_id} className="rounded p-3 text-sm" style={{ background: C.sand, border: `1px solid ${C.divider}` }}>
                <div className="font-medium" style={{ color: C.charcoal }}>{a.description}</div>
                <div className="text-xs mt-1" style={{ color: C.slate }}>{a.amended_by_name} · {fmtDateTime(a.amended_at)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Payments tab ──────────────────────────────────────────────────────────────
function PaymentsTab({ booking, canManage, onRefresh }) {
  const [showAdd, setShowAdd] = useState(false);
  const milestones = booking.payment_milestones || [];

  const handleStatusUpdate = async (mid, newStatus) => {
    try {
      await bookingApi.updateMilestone(booking.id, mid, { status: newStatus });
      toast.success("Milestone updated");
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    }
  };

  return (
    <div className="p-6 space-y-4">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: "Total Milestones",       value: fmtCurrency(booking.milestones_total, booking.currency) },
          { label: "Paid / Waived",          value: fmtCurrency(booking.milestones_paid, booking.currency) },
          { label: "Remaining",              value: fmtCurrency((booking.milestones_total || 0) - (booking.milestones_paid || 0), booking.currency) },
        ].map(s => (
          <div key={s.label} className="rounded-lg p-3 text-center" style={{ background: C.sand, border: `1px solid ${C.divider}` }}>
            <div className="text-xs mb-1" style={{ color: C.slate }}>{s.label}</div>
            <div className="text-sm font-semibold" style={{ color: C.charcoal }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Milestone list */}
      {milestones.length === 0 ? (
        <div className="text-sm text-center py-8" style={{ color: C.slate }}>No payment milestones defined</div>
      ) : (
        <div className="space-y-2">
          {milestones.map(m => {
            const cfg = MILESTONE_STATUS_CONFIG[m.status] || MILESTONE_STATUS_CONFIG.PENDING;
            return (
              <div key={m.milestone_id} data-testid={`milestone-${m.milestone_id}`}
                className="rounded-lg p-4 flex items-start justify-between gap-3"
                style={{ background: C.sand, border: `1px solid ${C.divider}` }}
              >
                <div>
                  <div className="text-sm font-medium" style={{ color: C.charcoal }}>{m.name}</div>
                  {m.description && <div className="text-xs mt-0.5" style={{ color: C.slate }}>{m.description}</div>}
                  {m.due_date && <div className="text-xs mt-1" style={{ color: C.slate }}>Due: {m.due_date}</div>}
                  {m.invoice_ref && <div className="text-xs" style={{ color: C.slate }}>Invoice: {m.invoice_ref}</div>}
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <div className="text-sm font-semibold" style={{ color: C.charcoal }}>
                    {fmtCurrency(m.amount, booking.currency)}
                  </div>
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ background: cfg.bg, color: cfg.text }}>
                    {cfg.label}
                  </span>
                  {canManage && m.status === "PENDING" && (
                    <button
                      className="text-xs underline"
                      style={{ color: C.gold }}
                      onClick={() => handleStatusUpdate(m.milestone_id, "PAID")}
                    >
                      Mark Paid
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {canManage && !["COMPLETED", "CANCELLED"].includes(booking.status) && (
        <Button
          data-testid="add-milestone-modal-btn"
          size="sm"
          variant="outline"
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-1.5"
        >
          <Plus className="w-3 h-3" /> Add Milestone
        </Button>
      )}

      {showAdd && (
        <AddMilestoneModal
          bookingId={booking.id}
          onClose={() => setShowAdd(false)}
          onSuccess={onRefresh}
        />
      )}
    </div>
  );
}

// ── Event Linkage tab ─────────────────────────────────────────────────────────
function EventLinkageTab({ booking }) {
  return (
    <div className="p-6 space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="rounded-lg p-4" style={{ background: C.sand, border: `1px solid ${C.divider}` }}>
          <div className="text-xs mb-1" style={{ color: C.slate }}>Opportunity</div>
          <Link
            to={`/crm/opportunities/${booking.opportunity_id}`}
            className="text-sm font-medium hover:underline flex items-center gap-1"
            style={{ color: C.gold }}
          >
            {booking.opportunity_name || booking.opportunity_id} <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
        {booking.event_master_id && (
          <div className="rounded-lg p-4" style={{ background: C.sand, border: `1px solid ${C.divider}` }}>
            <div className="text-xs mb-1" style={{ color: C.slate }}>Linked Event</div>
            <Link
              to={`/crm/events/${booking.event_master_id}`}
              className="text-sm font-medium hover:underline flex items-center gap-1"
              style={{ color: C.gold }}
            >
              Event Workspace <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
        )}
        {!booking.event_master_id && (
          <div className="rounded-lg p-4 text-sm" style={{ background: C.sand, color: C.slate, border: `1px solid ${C.divider}` }}>
            No event linked yet. Link created when Opportunity converts to Event.
          </div>
        )}
      </div>
    </div>
  );
}

// ── Activity tab ──────────────────────────────────────────────────────────────
function ActivityTab({ booking }) {
  const trail = [...(booking.audit_trail || [])].reverse();
  return (
    <div className="p-6">
      {trail.length === 0 ? (
        <div className="text-sm text-center py-8" style={{ color: C.slate }}>No activity yet</div>
      ) : (
        <div className="space-y-3">
          {trail.map((e, i) => (
            <div key={i} className="flex gap-3 text-sm">
              <div className="w-2 h-2 rounded-full mt-1.5 shrink-0" style={{ background: C.gold }} />
              <div>
                <div className="font-medium" style={{ color: C.charcoal }}>{e.action?.replace(/_/g, " ")}</div>
                {e.notes && <div className="text-xs mt-0.5" style={{ color: C.slate }}>{e.notes}</div>}
                <div className="text-xs mt-0.5" style={{ color: C.slate }}>
                  {e.actor_name || e.actor_id} · {fmtDateTime(e.timestamp)}
                </div>
                {e.from_status && e.to_status && (
                  <div className="text-xs mt-0.5" style={{ color: C.slate }}>
                    {e.from_status} → {e.to_status}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function BookingDetail() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [showConfirm, setShowConfirm] = useState(false);
  const [showCancel, setShowCancel] = useState(false);

  const canConfirm = hasCapability(user, "booking.confirm");
  const canManage  = hasCapability(user, "booking.manage");
  const canCancel  = hasCapability(user, "booking.cancel");

  const fetchBooking = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await bookingApi.get(bookingId);
      setBooking(data);
    } catch (e) {
      toast.error("Failed to load booking");
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => { fetchBooking(); }, [fetchBooking]);

  if (!hasCapability(user, "booking.read")) {
    return (
      <div className="p-8 text-center" style={{ color: C.slate }}>
        You don't have permission to view bookings.
      </div>
    );
  }

  if (loading) return (
    <div className="flex items-center justify-center h-64" style={{ color: C.slate }}>
      <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading booking...
    </div>
  );

  if (!booking) return (
    <div className="p-8 text-center" style={{ color: C.slate }}>Booking not found</div>
  );

  const handleSubmit = async () => {
    try {
      await bookingApi.submit(booking.id);
      toast.success("Booking submitted for acceptance");
      fetchBooking();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit");
    }
  };

  const handleComplete = async () => {
    try {
      await bookingApi.complete(booking.id);
      toast.success("Booking marked as completed");
      fetchBooking();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to complete");
    }
  };

  const statusCfg = STATUS_CONFIG[booking.status] || STATUS_CONFIG.DRAFT;

  // Workflow action bar — consolidates DRAFT/PENDING_ACCEPTANCE/CONFIRMED actions
  const primaryAction = (() => {
    if (booking.status === "DRAFT" && canManage)
      return {
        label: "Submit for Acceptance",
        testId: "submit-booking-btn",
        style: { background: C.gold, color: "#fff" },
        onClick: handleSubmit,
      };
    if (booking.status === "PENDING_ACCEPTANCE" && canConfirm)
      return {
        label: "Confirm Booking",
        testId: "confirm-booking-action-btn",
        style: { background: "#065F46", color: "#fff" },
        onClick: () => setShowConfirm(true),
      };
    if (booking.status === "CONFIRMED" && canManage)
      return {
        label: "Mark Completed",
        testId: "complete-booking-btn",
        style: { background: "#1E40AF", color: "#fff" },
        onClick: handleComplete,
      };
    return null;
  })();

  const cancelSecondary = !["COMPLETED","CANCELLED"].includes(booking.status) && canCancel
    ? { label: "Cancel Booking", testId: "cancel-booking-btn", className: "border-red-200 text-red-600 hover:bg-red-50", onClick: () => setShowCancel(true) }
    : null;

  const workflowHint = {
    DRAFT:              "Review booking details before submitting for customer acceptance.",
    PENDING_ACCEPTANCE: "Awaiting customer confirmation. Confirm once the customer has accepted.",
    CONFIRMED:          "Booking is confirmed. Mark as completed once the event is delivered.",
  }[booking.status];

  return (
    <div data-testid="booking-detail-page" className="min-h-screen" style={{ background: C.ivory }}>
      {/* Header */}
      <div className="sticky top-0 z-10 px-6 py-4" style={{ background: C.ivory, borderBottom: `1px solid ${C.divider}` }}>
        <div className="max-w-5xl mx-auto">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="p-1.5 rounded-full hover:bg-black/5 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" style={{ color: C.slate }} />
              </button>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="font-semibold text-lg" style={{ color: C.charcoal }}>
                    {booking.booking_number}
                  </h1>
                  <StatusBadge status={booking.status} />
                </div>
                <div className="text-xs mt-0.5" style={{ color: C.slate }}>
                  {booking.opportunity_name || "Booking"} · {fmtCurrency(booking.total_value, booking.currency)}
                </div>
              </div>
            </div>
            {/* Refresh only — action buttons moved to WorkflowActionBar */}
            <button onClick={fetchBooking} className="p-1.5 rounded hover:bg-black/5 flex-shrink-0">
              <RefreshCw className="w-4 h-4" style={{ color: C.slate }} />
            </button>
          </div>

          {/* Booking lifecycle stage bar */}
          <div className="mt-2 -mx-2 border-t pt-1" style={{ borderColor: C.divider }}>
            <EventStageBar
              stages={BOOKING_STAGES}
              current={booking.status}
              terminal={BOOKING_TERMINAL}
              accentHex={C.gold}
            />
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mt-2 overflow-x-auto">
            {TABS.map(t => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  data-testid={`booking-tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full whitespace-nowrap transition-all"
                  style={{
                    background: active ? C.gold : "transparent",
                    color:      active ? "#fff" : C.slate,
                    border:     `1px solid ${active ? C.gold : C.divider}`,
                  }}
                >
                  <Icon size={11} />
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className={`max-w-5xl mx-auto ${primaryAction ? "pb-20" : ""}`}>
        {/* CRM-UX-03: Business Owner Intervention Banner */}
        {bookingId && (
          <div className="px-6 pt-3">
            <InterventionBanner recordType="booking" recordId={bookingId} />
          </div>
        )}
        {tab === "overview" && <OverviewTab booking={booking} />}
        {tab === "contract" && <ContractTab booking={booking} />}
        {tab === "payments" && <PaymentsTab booking={booking} canManage={canManage} onRefresh={fetchBooking} />}
        {tab === "event"    && <EventLinkageTab booking={booking} />}
        {tab === "activity" && <ActivityTab booking={booking} />}
      </div>

      {showConfirm && (
        <ConfirmBookingModal
          booking={booking}
          onClose={() => setShowConfirm(false)}
          onSuccess={fetchBooking}
        />
      )}
      {showCancel && (
        <CancelBookingModal
          booking={booking}
          onClose={() => setShowCancel(false)}
          onSuccess={fetchBooking}
        />
      )}

      {/* Workflow Action Bar */}
      <WorkflowActionBar
        visible={!!primaryAction}
        primary={primaryAction}
        secondary={cancelSecondary}
        hint={workflowHint}
      />
    </div>
  );
}
